export interface TimelineSubRowItem {
  uid: string;
  onPageStartTime: number;
  timelineSubRow: number;
}

// Tolerance for floating-point comparisons. Times are rounded to 2 decimal places
// (centiseconds), so 1ms is well below the rounding granularity but large enough
// to absorb IEEE-754 drift from arithmetic on those rounded values.
const OVERLAP_EPSILON = 0.001;

function hasOverlapInRow<T extends TimelineSubRowItem>(item: T, row: number, rowMap: Map<number, T[]>, getEndTime: (item: T) => number): boolean {
  return (rowMap.get(row) ?? []).some(
    (existing) =>
      existing.uid !== item.uid &&
      item.onPageStartTime < getEndTime(existing) - OVERLAP_EPSILON &&
      getEndTime(item) > existing.onPageStartTime + OVERLAP_EPSILON
  );
}

export function recalculateTimelineSubRows<T extends TimelineSubRowItem>(items: T[], getEndTime: (item: T) => number): void {
  if (items.length === 0) {
    return;
  }

  const sorted = [...items].sort((a, b) => {
    if (a.timelineSubRow !== b.timelineSubRow) {
      return a.timelineSubRow - b.timelineSubRow;
    }
    return a.onPageStartTime - b.onPageStartTime;
  });

  const rowMap = new Map<number, T[]>();

  for (const item of sorted) {
    let assignedRow = item.timelineSubRow;

    if (hasOverlapInRow(item, assignedRow, rowMap, getEndTime)) {
      assignedRow = 0;
      while (hasOverlapInRow(item, assignedRow, rowMap, getEndTime)) {
        assignedRow += 1;
      }
    }

    item.timelineSubRow = assignedRow;
    const rowItems = rowMap.get(assignedRow) ?? [];
    rowItems.push(item);
    rowMap.set(assignedRow, rowItems);
  }

  // Compact: remap used rows to eliminate gaps
  const usedRows = [...new Set(items.map((item) => item.timelineSubRow))].sort((a, b) => a - b);
  const rowMapping = new Map<number, number>();
  usedRows.forEach((row, index) => {
    rowMapping.set(row, index);
  });

  for (const item of items) {
    item.timelineSubRow = rowMapping.get(item.timelineSubRow) ?? 0;
  }
}
